import { z } from 'zod';
import { Money } from './money';

export const StockRow = z.object({
  productId: z.string(),
  name: z.string(),
  unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']),
  supplied: z.number(),
  /** расход: прямые продажи + уход через тех-карты блюд */
  used: z.number(),
  writtenOff: z.number(),
  balance: z.number(),
  avgCost: Money.nullable(),
  stockValue: Money.nullable(),
  /** остаток / средний дневной расход 14 дней; null — расхода нет */
  daysLeft: z.number().nullable().optional(),
  /** категория продукта — частичная инвентаризация по одной полке */
  categoryName: z.string().nullable(),
});
export const StockBalance = z.object({
  rows: z.array(StockRow),
  totalValue: Money,
});
export type StockBalance = z.infer<typeof StockBalance>;

export const SupplyLine = z.object({
  productId: z.string(),
  qty: z.number().positive().max(100000),
  unitCost: Money.min(0),
});
export const CreateSupply = z.object({
  locationId: z.string(),
  supplier: z.string().max(60).optional(),
  comment: z.string().max(200).optional(),
  items: z.array(SupplyLine).min(1).max(100),
});
export type CreateSupply = z.infer<typeof CreateSupply>;

export const WriteOffReason = z.enum(['SPOILAGE', 'LOSS', 'STAFF_MEAL', 'OTHER']);
export type WriteOffReason = z.infer<typeof WriteOffReason>;

export const WriteOffLine = z.object({
  productId: z.string(),
  qty: z.number().positive().max(100000),
});
export const CreateWriteOff = z.object({
  locationId: z.string(),
  reason: WriteOffReason,
  comment: z.string().max(200).optional(),
  items: z.array(WriteOffLine).min(1).max(100),
});
export type CreateWriteOff = z.infer<typeof CreateWriteOff>;

/** Лента документов склада: приходы и списания вперемешку по времени. */
export const StockDocRow = z.object({
  id: z.string(),
  kind: z.enum(['SUPPLY', 'WRITEOFF', 'INVENTORY']),
  at: z.string().datetime(),
  title: z.string(),
  itemsCount: z.number().int(),
  total: Money.nullable(), // сумма прихода; у списания null
});
export const StockDocs = z.object({ rows: z.array(StockDocRow) });
export type StockDocs = z.infer<typeof StockDocs>;

export const CreateIngredient = z.object({
  name: z.string().min(1).max(80),
  unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']),
});
export type CreateIngredient = z.infer<typeof CreateIngredient>;

export const RecipeLineDto = z.object({
  ingredientId: z.string(),
  qty: z.number().positive().max(10000),
});
export const RecipeItems = z.object({
  items: z.array(RecipeLineDto).max(50),
});
export type RecipeItems = z.infer<typeof RecipeItems>;

/** История закупочных цен позиции — последние партии. */
export const StockLotRow = z.object({
  at: z.string().datetime(),
  qty: z.number(),
  unitCost: Money,
  supplier: z.string().nullable(),
});
export const StockLots = z.object({
  rows: z.array(StockLotRow),
  avgCost: Money.nullable(),
});
export type StockLots = z.infer<typeof StockLots>;

export const RecipesExport = z.object({
  rows: z.array(z.object({
    dish: z.string(),
    ingredient: z.string(),
    qty: z.number(),
    unit: z.enum(['PIECE', 'KG', 'L', 'PORTION']),
  })),
});
export type RecipesExport = z.infer<typeof RecipesExport>;

/** Инвентаризация: пустая строка в UI = позицию не считали. */
export const InventoryLine = z.object({
  productId: z.string(),
  actual: z.number().min(0).max(100000),
});
export const CreateInventory = z.object({
  locationId: z.string(),
  items: z.array(InventoryLine).min(1).max(300),
});
export type CreateInventory = z.infer<typeof CreateInventory>;

export const InventoryResult = z.object({
  ok: z.literal(true),
  adjusted: z.number().int(),
  shortageValue: Money,
  surplusValue: Money,
});
export type InventoryResult = z.infer<typeof InventoryResult>;
